import React, { useState, useEffect, useReducer, memo } from 'react';
import { Landmark, Wallet, CreditCard, Building2, Hash, User, Save, CheckCircle, X } from 'lucide-react';
// import lib
import handleReducer, { textChange, onBlur } from '../lib/handleChange';
import toastAlert from '../lib/toastAlert';
import isEmpty from '../lib/isEmpty';
// import services
import { getBankDetails, withdrawApprove } from '../services/admin.service';


const initialBankvalue = {
    accountHolderName: '',
    bankName: '',
    branchName: '',
    ifscCode: '',
    accountNumber: '',
    isSaved: false
};

const initialUpivalue = {
    // UPI Details
    upiProvider: '',
    upiUsername: '',
    upiNumber: '',
    isSaved: false
};

const BankAccountModal = ({ isOpen, onClose, userId, withdrawId, withdrawStatus, fetchData }) => {
    // state
    const [paymentType, setPaymentType] = useState('bank');
    const [bankDetails, setBankDetails] = useReducer(handleReducer, initialBankvalue);
    const [upiDetails, setUpiDetails] = useReducer(handleReducer, initialUpivalue);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [loader, setLoader] = useState(false);

    const { accountHolderName, bankName, branchName, ifscCode, accountNumber } = bankDetails;
    const { upiProvider, upiUsername, upiNumber } = upiDetails;

    // function
    const handleChange = e => textChange(e, paymentType == 'bank' ? setBankDetails : setUpiDetails);
    const handleBlur = e => onBlur(e, setTouched);

    const handlePaymentTypeChange = (type) => {
        setPaymentType(type);
        setErrors({});
        setTouched({});
    };

    const handleSubmit = async () => {
        try {
            setLoader(true)
            const { status, message } = await withdrawApprove({ withdrawId });
            if (status == true) {
                toastAlert('success', message, 'withdrawApprove')
                setLoader(false);
                onClose();
                fetchData();
            } else if (!isEmpty(message)) {
                toastAlert('error', message, 'withdrawApprove')
                setLoader(false)
            }
        } catch (error) {
            setLoader(false)
        }
    }

    const initialCall = async () => {
        try {
            const { status, result } = await getBankDetails(userId);
            if (status === true) {
                setBankDetails({ type: 'INITIAL_SET', value: result?.bankDetails });
                setUpiDetails({ type: 'INITIAL_SET', value: result?.upiDetails });
            }
        } catch (error) { }
    }

    useEffect(() => {
        if (isOpen) {
            initialCall();
        }
    }, [isOpen]);

    // Close modal when clicking outside
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Close modal on ESC key
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.keyCode === 27 && isOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscKey);
        return () => document.removeEventListener('keydown', handleEscKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm"
            onClick={handleBackdropClick}
        >
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 p-6 sticky top-0 z-10">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full">
                                <Landmark className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-800">Bank Account Details</h1>
                                {/* <p className="text-gray-600 text-sm">Add or update your payment information</p> */}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
                    <div className="p-6">
                        <form className="space-y-6">
                            {/* Payment Type Selection */}
                            <div>
                                <label className="block text-lg font-semibold text-gray-800 mb-4">
                                    Select Payment Method
                                </label>
                                <div className="grid md:grid-cols-2 gap-4">
                                    {/* Bank Details Radio */}
                                    <div
                                        onClick={() => handlePaymentTypeChange('bank')}
                                        className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${paymentType === 'bank'
                                            ? 'border-cyan-500 bg-cyan-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0">
                                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${paymentType === 'bank'
                                                    ? 'border-cyan-500 bg-cyan-500'
                                                    : 'border-gray-300'
                                                    }`}>
                                                    {paymentType === 'bank' && (
                                                        <div className="w-3 h-3 bg-white rounded-full"></div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Building2 className="w-5 h-5 text-cyan-600" />
                                                    <h3 className="font-semibold text-gray-800">Bank Details</h3>
                                                </div>
                                                <p className="text-sm text-gray-600">
                                                    Add your bank account information for direct transfers
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* UPI Payment Radio */}
                                    <div
                                        onClick={() => handlePaymentTypeChange('upi')}
                                        className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${paymentType === 'upi'
                                            ? 'border-cyan-500 bg-cyan-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0">
                                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${paymentType === 'upi'
                                                    ? 'border-cyan-500 bg-cyan-500'
                                                    : 'border-gray-300'
                                                    }`}>
                                                    {paymentType === 'upi' && (
                                                        <div className="w-3 h-3 bg-white rounded-full"></div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Wallet className="w-5 h-5 text-cyan-600" />
                                                    <h3 className="font-semibold text-gray-800">UPI Payment</h3>
                                                </div>
                                                <p className="text-sm text-gray-600">
                                                    Add your UPI details for instant payments
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bank Details Form */}
                            {paymentType === 'bank' && (
                                <div className="space-y-5 border-t pt-6">
                                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                        <Building2 className="w-5 h-5 text-cyan-600" />
                                        Bank Account Information
                                    </h3>

                                    {/* Account Holder Name */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Account Holder Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                name="accountHolderName"
                                                value={accountHolderName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                disabled={true}
                                                placeholder="Enter account holder name"
                                                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.accountHolderName && touched.accountHolderName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                        </div>
                                        {errors.accountHolderName && touched.accountHolderName && (
                                            <p className="text-red-500 text-sm mt-1">{errors.accountHolderName}</p>
                                        )}
                                    </div>

                                    {/* Bank Name */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Bank Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                name="bankName"
                                                value={bankName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                disabled={true}
                                                placeholder="Enter bank name"
                                                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.bankName && touched.bankName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                        </div>
                                        {errors.bankName && touched.bankName && (
                                            <p className="text-red-500 text-sm mt-1">{errors.bankName}</p>
                                        )}
                                    </div>

                                    {/* Branch Name */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Branch Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                name="branchName"
                                                value={branchName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                disabled={true}
                                                placeholder="Enter branch name"
                                                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.branchName && touched.branchName ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                        </div>
                                        {errors.branchName && touched.branchName && (
                                            <p className="text-red-500 text-sm mt-1">{errors.branchName}</p>
                                        )}
                                    </div>

                                    {/* IFSC Code */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            IFSC Code <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                name="ifscCode"
                                                value={ifscCode}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                disabled={true}
                                                placeholder="e.g., ICIC0006216"
                                                maxLength={11}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-lg uppercase focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.ifscCode && touched.ifscCode ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                        </div>
                                        {errors.ifscCode && touched.ifscCode && (
                                            <p className="text-red-500 text-sm mt-1">{errors.ifscCode}</p>
                                        )}
                                    </div>

                                    {/* Account Number */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Account Number <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                name="accountNumber"
                                                value={accountNumber}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                disabled={true}
                                                placeholder="Enter account number"
                                                maxLength={18}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.accountNumber && touched.accountNumber ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                        </div>
                                        {errors.accountNumber && touched.accountNumber && (
                                            <p className="text-red-500 text-sm mt-1">{errors.accountNumber}</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* UPI Payment Form */}
                            {paymentType === 'upi' && (
                                <div className="space-y-5 border-t pt-6">
                                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                        <Wallet className="w-5 h-5 text-cyan-600" />
                                        UPI Payment Information
                                    </h3>

                                    {/* UPI Provider */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            UPI Provider <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Wallet className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <select
                                                name="upiProvider"
                                                value={upiProvider}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                disabled={true}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.upiProvider && touched.upiProvider ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            >
                                                <option value="">Select UPI provider</option>
                                                <option value="GPay">Google Pay (GPay)</option>
                                                <option value="PhonePe">PhonePe</option>
                                            </select>
                                        </div>
                                        {errors.upiProvider && touched.upiProvider && (
                                            <p className="text-red-500 text-sm mt-1">{errors.upiProvider}</p>
                                        )}
                                    </div>

                                    {/* UPI Username */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            {upiProvider} Username <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                name="upiUsername"
                                                value={upiUsername}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                disabled={true}
                                                placeholder="Enter username"
                                                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.upiUsername && touched.upiUsername ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                        </div>
                                        {errors.upiUsername && touched.upiUsername && (
                                            <p className="text-red-500 text-sm mt-1">{errors.upiUsername}</p>
                                        )}
                                    </div>

                                    {/* UPI Number */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            UPI Number (Mobile) <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                name="upiNumber"
                                                value={upiNumber}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                disabled={true}
                                                placeholder="Enter 10-digit mobile number"
                                                maxLength={10}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.upiNumber && touched.upiNumber ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                        </div>
                                        {errors.upiNumber && touched.upiNumber && (
                                            <p className="text-red-500 text-sm mt-1">{errors.upiNumber}</p>
                                        )}
                                    </div>
                                </div>
                            )}
                            {
                                withdrawStatus == 'Pending' && (
                                    <div className="border-t pt-6">
                                        <button
                                            type="button"
                                            disabled={loader}
                                            className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                            onClick={handleSubmit}
                                        >
                                            {loader ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    Loading...
                                                </>
                                            ) : (
                                                <>
                                                    Approve
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )
                            }
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(BankAccountModal);